import type { BillingPlanUpgradeSource } from "@editor/reducers/api-reducer";
import type { V1PlanInfo, V2PlanInfo } from "schemas/billing";
import type { V1BillingTier, V2BillingTier } from "schemas/generated/billing";
import type { Workspace } from "schemas/generated/workspace";

import React from "react";

import { useSubscriptionDetails } from "@editor/hooks/subscription";

import V1PlanCard from "../v1/V1PlanCard";
import V2PlanCard from "../v2/V2PlanCard";
import { getPlanRank } from "./sharedBillingUtils";

type PlanCardProps =
  | {
      reploVersion: "v1";
      plan: V1PlanInfo;
      currentPlan: V1BillingTier;
      isYearly: boolean;
      workspace: Workspace;
      source: BillingPlanUpgradeSource;
    }
  | {
      reploVersion: "v2";
      plan: V2PlanInfo;
      currentPlan: V2BillingTier;
      isYearly: boolean;
      workspace: Workspace;
      source: BillingPlanUpgradeSource;
    };

const PlanCard = ({
  reploVersion,
  plan,
  currentPlan,
  isYearly,
  workspace,
  source,
}: PlanCardProps) => {
  const { data: subscriptionDetails, isPending: isSubscriptionDetailsPending } =
    useSubscriptionDetails();

  const cardPlanTier = plan.tier;

  const isCurrentTier = currentPlan === cardPlanTier;
  const isCurrentlyTierMonthly =
    subscriptionDetails?.billingInterval === "monthly";
  const isYearlyIntervalUpgrade =
    isCurrentTier && isCurrentlyTierMonthly && isYearly;

  const isDowngrade =
    !isCurrentTier && getPlanRank(cardPlanTier) < getPlanRank(currentPlan);

  const isUpgrade = getPlanRank(cardPlanTier) > getPlanRank(currentPlan);

  const isCustomPlan =
    cardPlanTier === "custom" || cardPlanTier === "custom_v2";

  const billingInterval = isYearly ? "yearly" : "monthly";

  return reploVersion === "v1" ? (
    <V1PlanCard
      plan={plan}
      currentPlan={currentPlan}
      isYearly={isYearly}
      workspace={workspace}
      source={source}
      cardPlanTier={cardPlanTier as V1BillingTier}
      isDowngrade={isDowngrade}
      isUpgrade={isUpgrade}
      isCurrentTier={isCurrentTier}
      isYearlyIntervalUpgrade={isYearlyIntervalUpgrade}
      isCustomPlan={isCustomPlan}
      billingInterval={billingInterval}
      subscriptionDetails={subscriptionDetails ?? undefined}
      isSubscriptionDetailsPending={isSubscriptionDetailsPending}
    />
  ) : (
    <V2PlanCard
      plan={plan}
      currentTier={currentPlan}
      isYearly={isYearly}
      workspace={workspace}
      isCustomPlan={isCustomPlan}
      subscriptionDetails={subscriptionDetails ?? undefined}
      isDowngrade={isDowngrade}
      isCurrentTier={isCurrentTier}
      isYearlyIntervalUpgrade={isYearlyIntervalUpgrade}
      billingInterval={billingInterval}
      isViewOnly={false}
      isSubscriptionDetailsPending={isSubscriptionDetailsPending}
    />
  );
};

export default PlanCard;
